/**
 * Large UPS edit form body — single "Large UPS" details card (#996 Phase 4).
 * Hand-written for the 7 per-instance large UPS fields (NOT a mechanical mirror of the
 * Cooling field set). Every field is wrapped in PermissionAwareField for RBAC.
 *
 * Step-level `powerAdditionalInformation` renders here via {@link RepeatableInstanceAdditionalInfo}
 * when the standalone Large UPS step is scoped (#1154 / #1330).
 */

import React from 'react';
import { Platform, StyleSheet } from 'react-native';

import {
  LARGE_UPS_ADDITIONAL_INFORMATION_LABEL,
  LARGE_UPS_ADDITIONAL_INFORMATION_PLACEHOLDER,
  LARGE_UPS_ADDITIONAL_INFORMATION_SUBTITLE,
  LARGE_UPS_EDIT_DETAILS_CARD_SUBTITLE,
  LARGE_UPS_EDIT_STATUS_EXISTING_LABEL,
  LARGE_UPS_EDIT_TYPE_LABEL,
  LARGE_UPS_EDIT_TYPE_OPTIONS,
} from '../../constants/largeUpsConfigurationsSubsectionCopy';
import { LARGE_UPS_PLACEHOLDERS } from '../../constants/largeUpsRequirementsUi';
import {
  LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
  LARGE_UPS_NOTES_SUBSECTION_FIELDS,
} from '../../constants/requestFieldDescriptors';
import { SECTION_IDS, getSectionLabel } from '../../constants/sectionIds';
import { useFormFieldVisibilityContext } from '../../hooks/useFormFieldVisibilityContext';
import { spacing } from '../../theme';
import {
  resolveRepeatableInstanceAttachmentSectionId,
  selectAttachmentsForRepeatableInstance,
  selectSelectedFilesForRepeatableInstance,
} from '../../utils/repeatableInstanceAttachmentSections';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import {
  FormRow,
  FormColumn,
  FormInput,
  FormYesNoChoice,
  FormTwoOptionChoice,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  StepForm,
} from '../forms';
import { LocationTagPicker } from '../locationTags/LocationTagPicker';
import { PermissionAwareField } from '../PermissionAwareField';
import { RepeatableInstanceAdditionalInfo } from '../repeatableSections/repeatableInstanceAdditionalInfo';

import type { SavedAttachment } from '../../services/attachmentService';
import type {
  LargeUpsStatus,
  LargeUpsConfiguration,
} from '../../types/largeUps';
import type { SelectedFile } from '../forms/FileUpload';

export interface LargeUpsEditFormAttachmentProps {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
}

export interface LargeUpsEditFormContentProps
  extends LargeUpsEditFormAttachmentProps {
  formData: Partial<LargeUpsConfiguration>;
  updateField: <K extends keyof LargeUpsConfiguration>(
    field: K,
    value: LargeUpsConfiguration[K],
  ) => void;
  disabled?: boolean;
  requestStage?: string;
  requestId?: string;
  testID?: string;
  /** Renders the per-instance Additional Information note when the step is scoped. */
  showAdditionalInformation?: boolean;
  showFullDetails?: boolean;
}

function largeUpsStatusToTypeLabel(status: LargeUpsStatus | undefined): string {
  return status === 'existing'
    ? LARGE_UPS_EDIT_TYPE_OPTIONS[0]
    : LARGE_UPS_EDIT_TYPE_OPTIONS[1];
}

function largeUpsTypeLabelToStatus(label: string): LargeUpsStatus {
  return label === LARGE_UPS_EDIT_STATUS_EXISTING_LABEL ? 'existing' : 'new';
}

function LargeUpsPermissionField({
  fieldId,
  requestStage,
  requestId,
  formDisabled,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  formDisabled: boolean;
  children: React.ReactNode;
}) {
  // Repeatable unit editor in mutation mode: list-level gate already enforced; field RBAC
  // against Facility Power Readiness keys caused "fake disabled" chrome (#997 / #1330).
  if (!requestStage || !formDisabled) {
    return <>{children}</>;
  }
  return (
    <PermissionAwareField
      fieldId={fieldId}
      formSection="Large UPS"
      requestStage={requestStage}
      requestId={requestId}
      containerStyle={formFieldInStack}
    >
      {children}
    </PermissionAwareField>
  );
}

const LARGE_UPS_EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export const LargeUpsEditFormContent: React.FC<
  LargeUpsEditFormContentProps
> = ({
  formData,
  updateField,
  disabled = false,
  requestStage,
  requestId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  testID = 'large-ups-edit-form',
  showAdditionalInformation = false,
  showFullDetails = true,
}) => {
  const permissionStage = requestStage ?? 'DRAFT';
  // Unit editor always renders the full per-instance field set; badge counts must
  // not use basic-view filtering (Create Request passes showFullDetails=false) — #996.
  const editorMetricsVisibility = useFormFieldVisibilityContext({
    showFullDetails: true,
    requestStage: permissionStage,
    requestId,
  });
  const largeUpsMetrics = subsectionHeaderMetricsProps(
    LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
    formData as Record<string, unknown>,
    editorMetricsVisibility,
  );
  const modelOfExistingUpsDisabled =
    disabled || formData.powerExistingSiteProtectedByLargeKwUps !== 'Yes';
  const configurationId = formData.id;
  const detailsBaseSectionId = SECTION_IDS.FACILITY_POWER_LARGE_UPS;
  const detailsSectionId = resolveRepeatableInstanceAttachmentSectionId(
    detailsBaseSectionId,
    configurationId,
  );
  const detailsAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const detailsSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const additionalSectionId = resolveRepeatableInstanceAttachmentSectionId(
    SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL,
    configurationId,
  );
  const attachmentsEnabled = true;

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <StepForm
        title={getSectionLabel(detailsBaseSectionId)}
        titleSubtitle={LARGE_UPS_EDIT_DETAILS_CARD_SUBTITLE}
        defaultSectionExpanded
        {...LARGE_UPS_EDITOR_SUBSECTION_PERSIST}
        {...largeUpsMetrics}
        sectionId={detailsSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={detailsAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(detailsSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={detailsSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(detailsSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="name"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Name"
                value={formData.name || ''}
                onChangeText={value => updateField('name', value)}
                placeholder="Enter a name for this large UPS (e.g., UPS A)"
                disabled={disabled}
                testID={`${testID}-name`}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="status"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormTwoOptionChoice
                label={LARGE_UPS_EDIT_TYPE_LABEL}
                value={largeUpsStatusToTypeLabel(formData.status)}
                options={[
                  LARGE_UPS_EDIT_TYPE_OPTIONS[0],
                  LARGE_UPS_EDIT_TYPE_OPTIONS[1],
                ]}
                onSelect={label =>
                  updateField('status', largeUpsTypeLabelToStatus(label))
                }
                disabled={disabled}
                inline
                testID={`${testID}-type`}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="locationTagId"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <LocationTagPicker
                requestId={requestId}
                requestStage={requestStage}
                value={formData.locationTagId ?? null}
                onChange={tagId => updateField('locationTagId', tagId)}
                disabled={disabled}
                testID={`${testID}-location-tag`}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerLargeUpsDescription"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Description"
                value={formData.powerLargeUpsDescription || ''}
                onChangeText={value =>
                  updateField('powerLargeUpsDescription', value)
                }
                placeholder={LARGE_UPS_PLACEHOLDERS.description || undefined}
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerExistingSiteProtectedByLargeKwUps"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormYesNoChoice
                label="Existing Site Protected by Large kW UPS?"
                value={formData.powerExistingSiteProtectedByLargeKwUps || ''}
                onSelect={v =>
                  updateField('powerExistingSiteProtectedByLargeKwUps', v)
                }
                disabled={disabled}
                inline
                testID={`${testID}-existing-site-protected`}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerModelOfExistingUps"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="If yes, model of existing UPS"
                value={formData.powerModelOfExistingUps || ''}
                onChangeText={value =>
                  updateField('powerModelOfExistingUps', value)
                }
                placeholder="Enter existing UPS model information"
                disabled={modelOfExistingUpsDisabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerLevelOfRedundancy"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Level of Redundancy"
                value={formData.powerLevelOfRedundancy || ''}
                onChangeText={value =>
                  updateField('powerLevelOfRedundancy', value)
                }
                placeholder={LARGE_UPS_PLACEHOLDERS.levelOfRedundancy}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerRequiredRedundancyForUps"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Required Redundancy for UPS"
                value={formData.powerRequiredRedundancyForUps || ''}
                onChangeText={value =>
                  updateField('powerRequiredRedundancyForUps', value)
                }
                placeholder={LARGE_UPS_PLACEHOLDERS.requiredRedundancyForUps}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerBatteryChemistry"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Battery Chemistry"
                value={formData.powerBatteryChemistry || ''}
                onChangeText={value =>
                  updateField('powerBatteryChemistry', value)
                }
                placeholder={LARGE_UPS_PLACEHOLDERS.batteryChemistry}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerBatteryRuntimeRequiredMin"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Battery Runtime Required (min)"
                value={formData.powerBatteryRuntimeRequiredMin || ''}
                onChangeText={value =>
                  updateField('powerBatteryRuntimeRequiredMin', value)
                }
                placeholder={LARGE_UPS_PLACEHOLDERS.batteryRuntimeRequiredMin}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>

        {/* #997 QA-A2: per-unit site-power fields moved off the shared Facility
            Power readiness card so each Large UPS unit owns its own value. */}
        <FormRow>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerSourceLocation"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Power Source location"
                value={formData.powerSourceLocation ?? ''}
                onChangeText={value =>
                  updateField('powerSourceLocation', value)
                }
                placeholder="Describe power source location"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
          <FormColumn>
            <LargeUpsPermissionField
              fieldId="powerFireSuppressionNeeds"
              requestStage={requestStage}
              requestId={requestId}
              formDisabled={disabled}
            >
              <FormInput
                label="Are there fire suppression needs?"
                value={formData.powerFireSuppressionNeeds ?? ''}
                onChangeText={value =>
                  updateField('powerFireSuppressionNeeds', value)
                }
                placeholder="Indicate if fire suppression is required"
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </LargeUpsPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      {showAdditionalInformation ? (
        <RepeatableInstanceAdditionalInfo
          titleSubtitle={LARGE_UPS_ADDITIONAL_INFORMATION_SUBTITLE}
          sectionId={additionalSectionId}
          fieldId="powerAdditionalInformation"
          inputLabel={LARGE_UPS_ADDITIONAL_INFORMATION_LABEL}
          placeholder={LARGE_UPS_ADDITIONAL_INFORMATION_PLACEHOLDER}
          value={String(formData.powerAdditionalInformation ?? '')}
          onChangeText={value =>
            updateField('powerAdditionalInformation', value)
          }
          requestId={requestId}
          requestStage={permissionStage}
          disabled={disabled}
          subsectionFields={LARGE_UPS_NOTES_SUBSECTION_FIELDS}
          headerValues={formData as Record<string, unknown>}
          attachmentsBySection={attachmentsBySection ?? {}}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles ?? {}}
          onFilesSelected={onFilesSelected}
          offlineMode={Boolean(isOffline)}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          showFullDetails={showFullDetails}
        />
      ) : null}
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
